import { useEffect, useMemo, useState } from 'react';
import ModalShell from './ModalShell.jsx';
import { api } from '../../../../lib/api.js';
import {
  CATEGORY_ORDER,
  paletteFor,
} from '../lib/categoryPalette.js';

const WINDOWS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '60 days', value: 60 },
  { label: '90 days', value: 90 },
];

// Topics modal — daily coverage chart over the last N days using the shared
// editor-dashboard category palette. Clicking a day fades the timeline out
// and animates a focused detail panel into view showing the per-category
// and per-source breakdown for that day. A back button restores the timeline.
export default function TopicsModal({ onClose }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState({ days: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/analytics/topics?days=${days}`)
      .then((res) => {
        if (cancelled) return;
        setData(res || { days: [], categories: [] });
        setSelectedDate(null);
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [days]);

  const categories = useMemo(
    () => sortCategories(data.categories || []),
    [data.categories]
  );

  const maxTotal = useMemo(
    () => Math.max(1, ...data.days.map((d) => d.total)),
    [data.days]
  );

  const selectedDay = useMemo(
    () =>
      selectedDate
        ? data.days.find((d) => d.date === selectedDate) || null
        : null,
    [data.days, selectedDate]
  );

  return (
    <ModalShell
      title="Topics"
      eyebrow={
        selectedDay
          ? `${formatPrettyDate(selectedDay.date)} · drill-in`
          : `${days}-day coverage`
      }
      onClose={onClose}
    >
      {/* Window pills — disabled while a day is focused so the chart doesn't
          rebuild from under the detail view. */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {WINDOWS.map((w) => (
          <button
            key={w.value}
            type="button"
            onClick={() => setDays(w.value)}
            disabled={Boolean(selectedDay)}
            className={[
              'text-[11px] font-bold uppercase tracking-display-sm px-3 py-1 rounded-full border transition-colors',
              days === w.value
                ? 'bg-hxd-ink text-white border-hxd-ink'
                : 'bg-white text-hxd-ink border-hxd-border hover:border-hxd-ink',
              selectedDay ? 'opacity-40 cursor-not-allowed' : '',
            ].join(' ')}
          >
            {w.label}
          </button>
        ))}
        {selectedDay && (
          <button
            type="button"
            onClick={() => setSelectedDate(null)}
            className="ml-auto text-[11px] font-bold uppercase tracking-display-sm text-hxd-orange hover:underline"
          >
            ← Back to {days}-day view
          </button>
        )}
      </div>

      {loading && <div className="text-[12px] text-hxd-muted">Loading…</div>}
      {error && <div className="text-[12px] text-hxd-err mb-3">{error}</div>}

      {!loading && data.days.length === 0 && (
        <div className="text-[12px] text-hxd-muted italic py-4">
          No editions in this window.
        </div>
      )}

      {!loading && data.days.length > 0 && (
        <div className="relative">
          {/* Timeline view — fades out when a day is selected. We render it
              behind the detail panel so transitions don't reflow the modal. */}
          <div
            className={[
              'transition-all duration-300 ease-out',
              selectedDay
                ? 'opacity-0 -translate-y-2 pointer-events-none absolute inset-0'
                : 'opacity-100 translate-y-0',
            ].join(' ')}
            aria-hidden={selectedDay ? 'true' : 'false'}
          >
            <Timeline
              days={data.days}
              maxTotal={maxTotal}
              categories={categories}
              selectedDate={selectedDate}
              onSelect={setSelectedDate}
            />
            <Legend categories={categories} />
            <p className="text-[11px] text-hxd-muted italic mt-2">
              Click a day for the source &amp; category drill-in.
            </p>
          </div>

          {/* Day detail — slides up into view after the timeline fades out. */}
          {selectedDay && (
            <DayDetail
              key={selectedDay.date}
              day={selectedDay}
              categories={categories}
            />
          )}
        </div>
      )}
    </ModalShell>
  );
}

function Timeline({ days, maxTotal, categories, selectedDate, onSelect }) {
  // Sparse x-axis labels: show every ~Nth date so we don't crowd the bottom.
  const labelEvery = Math.max(1, Math.floor(days.length / 6));

  return (
    <div>
      <div className="flex items-end gap-[3px] h-[200px] mb-1 px-1">
        {days.map((d, i) => (
          <DayBar
            key={d.date}
            day={d}
            maxTotal={maxTotal}
            categories={categories}
            isSelected={selectedDate === d.date}
            onClick={() => onSelect(d.date)}
            // Stagger entry animation per column so the chart "settles in"
            // instead of slamming in all at once.
            enterDelayMs={Math.min(i * 12, 240)}
          />
        ))}
      </div>
      <div className="border-t border-hxd-border-soft" />
      <div className="flex gap-[3px] px-1 mt-1 text-[9px] text-hxd-muted">
        {days.map((d, i) => (
          <div key={d.date} className="flex-1 min-w-0 text-center truncate">
            {i % labelEvery === 0 ? formatTick(d.date) : ' '}
          </div>
        ))}
      </div>
    </div>
  );
}

function DayBar({ day, maxTotal, categories, isSelected, onClick, enterDelayMs }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setMounted(true), enterDelayMs);
    return () => clearTimeout(id);
  }, [enterDelayMs]);

  const heightPct = (day.total / maxTotal) * 100;
  const targetHeight = mounted ? Math.max(heightPct, day.total > 0 ? 4 : 1.5) : 0;

  return (
    <button
      type="button"
      onClick={onClick}
      title={`${day.date} · ${day.total} ${day.total === 1 ? 'article' : 'articles'}`}
      className={[
        'relative flex-1 min-w-[5px] h-full flex flex-col-reverse rounded-t-[3px] overflow-hidden group transition-transform duration-150',
        'hover:scale-y-[1.04] hover:-translate-y-[1px]',
        isSelected ? 'ring-2 ring-hxd-orange ring-offset-1 ring-offset-white' : '',
      ].join(' ')}
      style={{
        height: `${targetHeight}%`,
        transition: 'height 420ms cubic-bezier(0.2, 0.7, 0.2, 1)',
        transformOrigin: 'bottom center',
      }}
    >
      {day.total === 0 && (
        <div className="absolute inset-x-0 bottom-0 h-[2px] bg-hxd-border" />
      )}
      {day.total > 0 &&
        categories.map((c) => {
          const n = day.buckets?.[c] || 0;
          if (n === 0) return null;
          const segPct = (n / day.total) * 100;
          const palette = paletteFor(c);
          return (
            <div
              key={c}
              style={{
                height: `${segPct}%`,
                backgroundColor: palette.bar,
              }}
            />
          );
        })}
    </button>
  );
}

function Legend({ categories }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
      {categories.map((c) => {
        const palette = paletteFor(c);
        return (
          <div
            key={c}
            className="flex items-center gap-1.5 text-[11px] text-hxd-ink"
          >
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: palette.bar }}
            />
            <span className="font-display font-bold uppercase tracking-display-sm">
              {c}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function DayDetail({ day, categories }) {
  // Mount animation — fade + slide up from below.
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const maxCat = Math.max(1, ...categories.map((c) => day.buckets?.[c] || 0));
  const sources = day.sources || [];
  const maxSrc = Math.max(1, ...sources.map((s) => s.n));

  return (
    <div
      className={[
        'transition-all ease-out',
        shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
      ].join(' ')}
      style={{ transitionDuration: '380ms', transitionDelay: '120ms' }}
    >
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <div>
          <div className="font-display text-[20px] font-bold text-hxd-ink leading-none">
            {formatPrettyDate(day.date)}
          </div>
          <div className="text-[12px] text-hxd-muted mt-1">
            {day.total} {day.total === 1 ? 'article' : 'articles'} ·{' '}
            {sources.length} source{sources.length === 1 ? '' : 's'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Panel title="By category">
          {categories.length === 0 ? (
            <Empty />
          ) : (
            <ul className="space-y-2 mt-2">
              {categories.map((c, i) => {
                const n = day.buckets?.[c] || 0;
                const palette = paletteFor(c);
                const widthPct = maxCat > 0 ? (n / maxCat) * 100 : 0;
                const share =
                  day.total > 0 ? Math.round((n / day.total) * 100) : 0;
                return (
                  <CategoryRow
                    key={c}
                    label={c}
                    count={n}
                    share={share}
                    palette={palette}
                    widthPct={widthPct}
                    delayMs={180 + i * 60}
                  />
                );
              })}
            </ul>
          )}
        </Panel>

        <Panel title={`By source · ${sources.length}`}>
          {sources.length === 0 ? (
            <Empty />
          ) : (
            <ul className="space-y-1.5 mt-2 max-h-[260px] overflow-y-auto pr-1">
              {sources.map((s, i) => {
                const widthPct = maxSrc > 0 ? (s.n / maxSrc) * 100 : 0;
                return (
                  <SourceRow
                    key={s.name}
                    name={s.name}
                    count={s.n}
                    widthPct={widthPct}
                    delayMs={180 + Math.min(i, 12) * 35}
                  />
                );
              })}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="hxd-card">
      <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-orange pb-2 border-b-2 border-hxd-orange">
        {title}
      </div>
      {children}
    </div>
  );
}

function CategoryRow({ label, count, share, palette, widthPct, delayMs }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setShown(true), delayMs);
    return () => clearTimeout(id);
  }, [delayMs]);

  return (
    <li
      className="grid items-center gap-3"
      style={{ gridTemplateColumns: '88px minmax(0, 1fr) auto' }}
    >
      <span className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-ink truncate">
        {label}
      </span>
      <div
        className="relative h-5 rounded-[2px] overflow-hidden"
        style={{ backgroundColor: palette.track }}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-[2px]"
          style={{
            width: shown ? `${widthPct}%` : '0%',
            backgroundColor: palette.bar,
            transition: 'width 520ms cubic-bezier(0.2, 0.7, 0.2, 1)',
          }}
        />
      </div>
      <span className="flex items-baseline gap-1.5 justify-end tabular-nums">
        <span
          className="font-mono text-[15px] font-bold leading-none"
          style={{ color: palette.ink }}
        >
          {count}
        </span>
        <span className="font-mono text-[10px] text-hxd-muted leading-none w-9 text-right">
          {share}%
        </span>
      </span>
    </li>
  );
}

function SourceRow({ name, count, widthPct, delayMs }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setShown(true), delayMs);
    return () => clearTimeout(id);
  }, [delayMs]);

  return (
    <li
      className="grid items-center gap-3"
      style={{ gridTemplateColumns: 'minmax(0, 1fr) 100px auto' }}
    >
      <span className="text-[12px] text-hxd-ink truncate">{name}</span>
      <div className="relative h-2 rounded-full overflow-hidden bg-hxd-surface border border-hxd-border-soft">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-hxd-orange"
          style={{
            width: shown ? `${widthPct}%` : '0%',
            transition: 'width 460ms cubic-bezier(0.2, 0.7, 0.2, 1)',
          }}
        />
      </div>
      <span className="font-mono text-[12px] font-bold text-hxd-ink tabular-nums">
        {count}
      </span>
    </li>
  );
}

function Empty() {
  return (
    <div className="text-[12px] text-hxd-muted italic py-3">No data.</div>
  );
}

function sortCategories(list) {
  return [...list].sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

function formatPrettyDate(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatTick(isoDate) {
  if (!isoDate) return '';
  const [, m, d] = isoDate.split('-').map(Number);
  if (!m || !d) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[m - 1]} ${d}`;
}
